import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { refreshToken } from "../store/slices/authSlice";

/**
 * Hook to automatically refresh token before expiration
 * Checks token expiry every minute and refreshes if needed
 */
export const useTokenRefresh = () => {
  const dispatch = useDispatch();
  const { token, isAuthenticated, isRefreshing } = useSelector(
    (state) => state.auth
  );
  const intervalRef = useRef(null);
  const checkRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      // Clear any existing intervals
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (checkRef.current) {
        clearTimeout(checkRef.current);
        checkRef.current = null;
      }
      return;
    }

    const checkAndRefreshToken = () => {
      const tokenExpiry = localStorage.getItem("tokenExpiry");
      const refreshTokenValue = localStorage.getItem("refreshToken");

      if (!tokenExpiry || !refreshTokenValue) {
        return;
      }

      const expiryTime = parseInt(tokenExpiry);
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

      // If token expires within 5 minutes, refresh it
      if (now >= expiryTime - fiveMinutes && !isRefreshing) {
        console.log("🔄 Token expiring soon, refreshing...");
        dispatch(refreshToken());
      }
    };

    // Check immediately
    checkAndRefreshToken();

    // Check every minute
    intervalRef.current = setInterval(checkAndRefreshToken, 60 * 1000);

    // Also set a timeout to refresh exactly 5 minutes before expiry
    const tokenExpiry = localStorage.getItem("tokenExpiry");
    if (tokenExpiry) {
      const expiryTime = parseInt(tokenExpiry);
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      const timeUntilRefresh = expiryTime - now - fiveMinutes;

      if (timeUntilRefresh > 0) {
        checkRef.current = setTimeout(() => {
          if (!isRefreshing) {
            console.log("🔄 Token refresh timeout reached, refreshing...");
            dispatch(refreshToken());
          }
        }, timeUntilRefresh);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (checkRef.current) {
        clearTimeout(checkRef.current);
      }
    };
  }, [isAuthenticated, token, dispatch, isRefreshing]);
};
